import { useQuizBrowserBack } from './quizHistoryGuard';

import { QUIZ_ROUTES } from "./quizRoutes";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QuestionBankList.css";
import "./QuizLeaderboard.css";
import HeaderProfileSection from "../HeaderProfileSection";
import MyWalletSidebar from "../MyWalletSidebar";
import { useQuizLeaderboard } from "../../hooks/useQuizLeaderboard";
import leaderboardLeft from "../../assets/images/profile1.png";
import leaderboardCenter from "../../assets/images/expert1.png";
import leaderboardRight from "../../assets/images/c1.png";
import crown from "../../assets/images/crown1.png";

import {
  buildQuizBackTarget,
  buildQuizFlowState,
  buildQuizListState,
  buildStartQuizState,
  getStoredQuizFlowState,
  persistQuizFlowState,
  navigateQuizBack,
  resolveQuizBackTarget,
} from "./quizFlowState";

function getTopPlayerByRank(topThree = [], rank) {
  return (
    topThree.find(
      (player) => Number(player?.rank) === Number(rank)
    ) || null
  );
}

function getTopPlayerBadge(rank) {
  if (rank === 1) return leaderboardCenter;
  if (rank === 2) return leaderboardLeft;
  return leaderboardRight;
}

function QuizLeaderboardShimmer() {
  return (
    <div className="leaderboard-screen" aria-hidden="true">
      <div className="leaderboard-header">
        <div className="event-info">
          <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__title" />
          <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__date" />
        </div>
        <span className="dashboard-skeleton dashboard-skeleton--button qbl-shimmer__state" />
      </div>

      <div className="leaderboard-content">
        <section className="leaderboard-card">
          <div className="top-three qbl-shimmer__topThree">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                className="top-player qbl-shimmer__topPlayer"
                key={index}
              >
                <span className="dashboard-skeleton dashboard-skeleton--thumb qbl-shimmer__topBadge" />
                <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__topName" />
                <span className="dashboard-skeleton dashboard-skeleton--button qbl-shimmer__topScore" />
              </div>
            ))}
          </div>

          <div className="rank-header">
            <span className="rank-heading">RANK LIST</span>
            <span className="performer-heading">
              Top Performers
            </span>
          </div>

          <div className="rank-list-wrapper">
            <div className="rank-list">
              {Array.from({ length: 10 }).map((_, index) => (
                <div
                  className="rank-row qbl-shimmer__row"
                  key={index}
                >
                  <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__rank" />
                  <span className="dashboard-skeleton dashboard-skeleton--thumb qbl-shimmer__avatar" />
                  <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__name" />
                  <span className="dashboard-skeleton dashboard-skeleton--button qbl-shimmer__score" />
                </div>
              ))}
            </div>

            <div className="rank-bottom-action">
              <div className="current-rank-card qbl-shimmer__currentCard">
                <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__rank" />
                <span className="dashboard-skeleton dashboard-skeleton--thumb qbl-shimmer__avatar" />

                <div className="current-rank-info">
                  <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__currentLabel" />
                  <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__currentName" />
                </div>

                <span className="dashboard-skeleton dashboard-skeleton--button qbl-shimmer__score" />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

const QuizLeaderboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const persistedFlowState = useMemo(
    () => getStoredQuizFlowState(),
    []
  );

  const flowState = useMemo(
    () => buildQuizFlowState(location.state, persistedFlowState),
    [location.state, persistedFlowState]
  );

  const quizData = flowState?.quizData || {};

  const quizId = Number(
    quizData?.quizId ||
    quizData?.quiz_id ||
    quizData?.id ||
    0
  );

  const initialStateId = Number(
    flowState?.state_id ||
    quizData?.statesId ||
    quizData?.states_id ||
    0
  );

  const assesmentBatchId = Number(
    flowState?.assesment_batch_id || 0
  );

  const listState = useMemo(
    () => buildQuizListState(flowState),
    [flowState]
  );

  const startQuizState = useMemo(
    () => buildStartQuizState(flowState),
    [flowState]
  );

  // Dropdown States
  const [open, setOpen] = useState(false);
  const [openQuiz, setOpenQuiz] = useState(false);
  const [openDate, setOpenDate] = useState(false);

  const [selectedStateId, setSelectedStateId] =
    useState(initialStateId);

  // Dropdown References
  const dropdownRef = useRef(null);
  const quizDropdownRef = useRef(null);
  const dateDropdownRef = useRef(null);
  const rankListRef = useRef(null);

  const leaderboardScreenState = useMemo(
    () => buildQuizFlowState(flowState),
    [flowState]
  );

  const resolvedBackTarget = resolveQuizBackTarget(leaderboardScreenState, QUIZ_ROUTES.leaderboard, (assesmentBatchId > 0
      ? buildQuizBackTarget(
          QUIZ_ROUTES.start,
          startQuizState
        )
      : buildQuizBackTarget(
          QUIZ_ROUTES.list,
          listState
        )));
  useQuizBrowserBack(resolvedBackTarget);

  const leaderboardQuery = useQuizLeaderboard(
    {
      ...(selectedStateId > 0
        ? { states_id: selectedStateId }
        : { states_id: 0 }),
      quiz_id: quizId,
      assesment_batch_id: assesmentBatchId,
    },
    {
      enabled: quizId > 0 && assesmentBatchId > 0,
    }
  );

  const firstPage =
    leaderboardQuery.data?.pages?.[0] || null;

  const players = useMemo(() => {
    const pages = leaderboardQuery.data?.pages || [];
    const seenPlayers = new Set();

    return pages.flatMap((page) =>
      (page.players || []).filter((player) => {
        const uniqueKey = `${Number(
          player?.rank || 0
        )}-${Number(player?.studentId || 0)}`;

        if (seenPlayers.has(uniqueKey)) {
          return false;
        }

        seenPlayers.add(uniqueKey);
        return true;
      })
    );
  }, [leaderboardQuery.data]);

  const states = firstPage?.states || [];

  const selectedState =
    states.find(
      (item) =>
        Number(item.id) === Number(selectedStateId)
    )?.name ||
    firstPage?.stateName ||
    "Select State";

  const firstPlayer = getTopPlayerByRank(
    firstPage?.topThree || [],
    1
  );

  const secondPlayer = getTopPlayerByRank(
    firstPage?.topThree || [],
    2
  );

  const thirdPlayer = getTopPlayerByRank(
    firstPage?.topThree || [],
    3
  );

  const currentStudent = firstPage?.currentStudent || null;

  useEffect(() => {
    persistQuizFlowState(leaderboardScreenState);
  }, [leaderboardScreenState]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }

      if (
        quizDropdownRef.current &&
        !quizDropdownRef.current.contains(event.target)
      ) {
        setOpenQuiz(false);
      }

      if (
        dateDropdownRef.current &&
        !dateDropdownRef.current.contains(event.target)
      ) {
        setOpenDate(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  useEffect(() => {
    const container = rankListRef.current;

    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      if (
        !leaderboardQuery.hasNextPage ||
        leaderboardQuery.isFetchingNextPage
      ) {
        return;
      }

      const remainingHeight =
        container.scrollHeight -
        container.scrollTop -
        container.clientHeight;

      if (remainingHeight < 120) {
        leaderboardQuery.fetchNextPage();
      }
    };

    container.addEventListener(
      "scroll",
      handleScroll
    );

    return () =>
      container.removeEventListener(
        "scroll",
        handleScroll
      );
  }, [leaderboardQuery]);



  const handleSelect = (state) => {
    setSelectedStateId(Number(state?.id || 0));
    setOpen(false);
    setOpenQuiz(false);
    setOpenDate(false);
  };

  const handleLoadMore = () => {
    if (
      !leaderboardQuery.hasNextPage ||
      leaderboardQuery.isFetchingNextPage
    ) {
      return;
    }

    leaderboardQuery.fetchNextPage();
  };

  if (!quizId || !assesmentBatchId) {
    return (
      <div className="qbp-status">
        Leaderboard session details are missing.
      </div>
    );
  }

  return (
    <>
      <style>{`
        .homeDashboard-wrapper .page-content {
          padding-right: 0;
          min-height: 0;
          height: calc(100vh - 35px);
          overflow-x: hidden;
        }

        .homeDashboard-wrapper .main-content {
          height: 100vh;
          overflow-x: hidden !important;
          box-sizing: border-box;
          padding-bottom: 0px;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }

        .layout-wrapper {
          background: #f8f9fa !important;
        }

        .page-padding {
          padding: 30px 25px 0 !important;
        }

       @media (max-width: 767px){
        .page-padding {
             padding: 30px 15px 0 !important;
        }
       }
      `}</style>

      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page page-padding fixed-page-btn">
          <div className="start-quiz qbl-main instruction-main">

            <div className="hideProfile-page">
              <div className="categories-page-header mb-0">
                <div>
                  <button
                    type="button"
                    className="categories-back-button"
                    onClick={() =>
                      navigateQuizBack(navigate, resolvedBackTarget)
                    }
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </div>

                <div>
                  <h4 className="d-flex categories-page-title"></h4>
                </div>

                <div>
                  <HeaderProfileSection />
                </div>
              </div>
            </div>

            <div className="leaderboard-screen">
              <div>
                <h1 className="qb-title">
                  Leaderboard
                </h1>

                <p className="qb-sub">
                  See how you rank among top learners.
                </p>
              </div>

              {leaderboardQuery.isLoading && !firstPage ? (
                <QuizLeaderboardShimmer />
              ) : (
                <>
                  {/* LEADERBOARD HEADER */}
                  <div className="leaderboard-header">

                    {/* State Dropdown */}
                    <div
                      className="state-dropdown"
                      ref={dropdownRef}
                    >
                      <button
                        type="button"
                        className={`state-dropdown-button ${
                          open ? "dropdown-open" : ""
                        }`}
                        onClick={() => {
                          setOpen((prev) => !prev);
                          setOpenQuiz(false);
                          setOpenDate(false);
                        }}
                        aria-expanded={open}
                      >
                        <i className="bi bi-geo-alt-fill state-location-icon"></i>

                        <span>
                          {selectedState}
                        </span>

                        <i
                          className={`bi bi-chevron-down dropdown-chevron ${
                            open ? "chevron-up" : ""
                          }`}
                        ></i>
                      </button>

                      {open && (
                        <div className="state-dropdown-menu">
                          {states.map((state) => (
                            <button
                              type="button"
                              key={state.id}
                              className={`state-dropdown-option ${
                                Number(selectedStateId) ===
                                Number(state.id)
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() =>
                                handleSelect(state)
                              }
                            >
                              {state.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Event Date Dropdown */}
                    <div
                      className="state-dropdown event-date-dropdown"
                      ref={dateDropdownRef}
                    >
                      <button
                        type="button"
                        className={`state-dropdown-button ${
                          openDate ? "dropdown-open" : ""
                        }`}
                        onClick={() => {
                          setOpenDate((prev) => !prev);
                          setOpen(false);
                          setOpenQuiz(false);
                        }}
                        aria-expanded={openDate}
                      >
                        <i className="bi bi-calendar3 state-location-icon"></i>

                        <span>
                          {firstPage?.batchDate || "-"}
                        </span>

                        <i
                          className={`bi bi-chevron-down dropdown-chevron ${
                            openDate ? "chevron-up" : ""
                          }`}
                        ></i>
                      </button>

                      {openDate && (
                        <div className="state-dropdown-menu date-dropdown-menu">
                          <button
                            type="button"
                            className="state-dropdown-option selected"
                            onClick={() => setOpenDate(false)}
                          >
                            {firstPage?.batchDate || "-"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* LEADERBOARD CONTENT */}
                 <div
  className="leaderboard-content"
  ref={rankListRef}
>
                    <section className="leaderboard-card">

                      {/* TOP THREE */}
                      <div className="top-three">

                        {/* SECOND PLAYER */}
                        <div className="top-player second">
                          <div className="badge-wrapper">
                            <img
                              src={
                                secondPlayer?.avatar ||
                                getTopPlayerBadge(2)
                              }
                              alt=""
                              className="top-badge"
                            />

                            <span className="rank-circle">
                              2
                            </span>
                          </div>

                          <div className="player-name">
                            {secondPlayer?.name || "-"}
                          </div>

                          <div className="player-score">
                            {secondPlayer?.score ?? 0}
                          </div>

                          <span className="player-points">
                            Points
                          </span>
                        </div>

                        {/* FIRST PLAYER */}
                        <div className="top-player first">
                          <span className="winner-crown">
                            <img src={crown} alt="" />
                          </span>

                          <div className="badge-wrapper">
                            <img
                              src={
                                firstPlayer?.avatar ||
                                getTopPlayerBadge(1)
                              }
                              alt=""
                              className="top-badge"
                            />

                            <span className="rank-circle">
                              1
                            </span>
                          </div>

                          <div className="player-name">
                            {firstPlayer?.name || "-"}
                          </div>

                          <div className="player-score">
                            {firstPlayer?.score ?? 0}
                          </div>

                          <span className="player-points">
                            Points
                          </span>
                        </div>

                        {/* THIRD PLAYER */}
                        <div className="top-player third">
                          <div className="badge-wrapper">
                            <img
                              src={
                                thirdPlayer?.avatar ||
                                getTopPlayerBadge(3)
                              }
                              alt=""
                              className="top-badge"
                            />

                            <span className="rank-circle">
                              3
                            </span>
                          </div>

                          <div className="player-name">
                            {thirdPlayer?.name || "-"}
                          </div>

                          <div className="player-score">
                            {thirdPlayer?.score ?? 0}
                          </div>

                          <span className="player-points">
                            Points
                          </span>
                        </div>
                      </div>

                      {/* RANK LIST */}
                      <div className="rank-list-wrapper">
                        <div className="rank-header">
                          <span className="rank-heading">
                            RANK LIST
                          </span>

                          <span className="performer-heading">
                            Top Performers
                          </span>
                        </div>

                        <div
                          className="rank-list"
                        >
                          {leaderboardQuery.isError ? (
                            <div className="leaderboard-status leaderboard-status--error">
                              {leaderboardQuery.error?.message ||
                                "Unable to load leaderboard."}
                            </div>
                          ) : null}

                          {!leaderboardQuery.isLoading &&
                          !leaderboardQuery.isError &&
                          !players.length ? (
                            <div className="leaderboard-status">
                              No leaderboard data available.
                            </div>
                          ) : null}

                          {players.map((player) => (
                            <div
                              className="rank-row"
                              key={`${player.rank}-${
                                player.studentId ||
                                player.name
                              }`}
                            >
                              <div className="rank-number">
                                {player.rank}
                              </div>

                              <img
                                className="small-badge"
                                src={
                                  player.avatar ||
                                  getTopPlayerBadge(3)
                                }
                                alt=""
                              />

                              <div className="rank-name">
                                {player.name}
                              </div>

                              <div className="rank-score">
                                {player.score}
                              </div>
                            </div>
                          ))}

                          {leaderboardQuery.isFetchingNextPage ? (
                            <div className="qbl-shimmer__more">
                              {Array.from({
                                length: 4,
                              }).map((_, index) => (
                                <div
                                  className="rank-row qbl-shimmer__row"
                                  key={`leaderboard-more-${index}`}
                                >
                                  <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__rank" />
                                  <span className="dashboard-skeleton dashboard-skeleton--thumb qbl-shimmer__avatar" />
                                  <span className="dashboard-skeleton dashboard-skeleton--line qbl-shimmer__name" />
                                  <span className="dashboard-skeleton dashboard-skeleton--button qbl-shimmer__score" />
                                </div>
                              ))}
                            </div>
                          ) : null}

                          {leaderboardQuery.hasNextPage &&
                          !leaderboardQuery.isFetchingNextPage ? (
                            <div className="qbl-loadMoreWrap">
                              <button
                                type="button"
                                className="qbl-loadMoreBtn"
                                onClick={handleLoadMore}
                              >
                                Load more ranks
                              </button>
                            </div>
                          ) : null}
                        </div>

                        {/* CURRENT RANK */}
                        <div className="rank-bottom-action">
                          <div className="current-rank-card">
                            <div className="current-rank-number">
                              {currentStudent?.rank || "-"}
                            </div>

                            <div className="current-rank-avatar">
                              <img
                                src={
                                  currentStudent?.avatar ||
                                  getTopPlayerBadge(1)
                                }
                                alt=""
                              />
                            </div>

                            <div className="current-rank-info">
                              <span>
                                Your Current rank
                              </span>

                              <strong>
                                {currentStudent?.name ||
                                  "Student"}
                              </strong>
                            </div>

                            <div className="current-rank-score">
                              {currentStudent?.score || 0}
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </>
              )}
            </div>
          </div>

          <MyWalletSidebar withTopOffset={false} />
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default QuizLeaderboard;
